import React, { SyntheticEvent, useEffect, useState } from "react";
import { default as i18n } from "i18next";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import Rating from "@mui/material/Rating";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import SaveIcon from "@mui/icons-material/Save";
import { ReplacePermissionLevel } from "../../enums/hardware-monitor/hardware-monitoring";
import ValidationResult from "../../interfaces/api/validation-result";
import HttpFetchStatus from "../../enums/api/http-fetch-status";
import SystemSerialNumber from "../../interfaces/general/system-serial-data";
import HardwareMonitoring from "../../interfaces/hardware-monitor/hardware-monitoring";

const ReplaceLifetime: React.FC<{
  component: HardwareMonitoring;
  replaceHardwareParams: {
    serialNumberData: SystemSerialNumber | null;
    replaceStatus?: HttpFetchStatus;
    onResetAndReplace?: (component: HardwareMonitoring, dateOfReplacement: { value: DateTime, validation: ValidationResult }, technician: string, rating: number, feedback: string) => void;
  }
  onChange?: (values: {
    dateOfReplacement: { value: DateTime; validation: ValidationResult };
    technician: string;
    rating: number;
    feedback: string;
  }) => void;
  hideSaveButton?: boolean;
}> = ({
  component,
  replaceHardwareParams,
  onChange,
  hideSaveButton
}) => {
  const { t } = useTranslation();

  const [dateOfReplacement, setDateOfReplacement] = useState<{ value: DateTime; validation: ValidationResult }>({
    value: DateTime.now(),
    validation: { isValid: true, error: null },
  });
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [technician, setTechnician] = useState("");

  useEffect(() => {
    if (onChange) {
      onChange({ dateOfReplacement, technician, rating, feedback });
    }
  }, [dateOfReplacement, technician, rating, feedback, onChange]);

  const dateOfReplacementDateTimeChangeHandler = (newDateTime: DateTime | null) => {
    if (newDateTime != null) {
      setDateOfReplacement((prevState) => ({ ...prevState, value: newDateTime }));
    }
  };

  const technicianChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTechnician(event?.target.value);
  };

  const ratingChangeHandler = (event: SyntheticEvent<Element, Event>, value: number | null) => {
    if (value != null) {
      setRating(value);
    }
  };

  const feedbackChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event?.target.value);
  };

  const resetAndReplaceClickHandler = (component: HardwareMonitoring, dateOfReplacement: { value: DateTime, validation: ValidationResult }, technician: string, rating: number, feedback: string) => {
    if (replaceHardwareParams.onResetAndReplace) {
      replaceHardwareParams.onResetAndReplace(component, dateOfReplacement, technician, rating, feedback);
    }
  }

  return (
    <React.Fragment>
      <Grid container spacing={2} p={2}>
        <Grid item xs={12}>
          <Typography>{t("reset-component-info")}</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextWithLabel label={t("label-serial-number")} text={replaceHardwareParams.serialNumberData?.serialNumber ?? ""} />
        </Grid>
        <Grid item xs={12}>
          <TextWithLabel label={t("label-replacement-component")} text={component.id.toString()} />
        </Grid>
        <Grid item xs={12}>
          <InputLabel shrink>{t("label-replacement-lifetimes")}</InputLabel>
          {component.types.map((monitoringType) => (
            <Typography key={monitoringType.typeId}>
              {t("hardware-monitoring-save-type." + monitoringType.typeId)}: {monitoringType.value}
            </Typography>
          ))}
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12} md={component.replacePermission === ReplacePermissionLevel.ServiceOnly ? 12 : 8}>
          <Stack gap={0} sx={{ width: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
              <DateTimePicker
                value={dateOfReplacement.value}
                label={t("label-date-of-replacement")}
                slotProps={{
                  textField: { variant: "standard", error: !dateOfReplacement.validation.isValid, helperText: dateOfReplacement.validation.error },
                }}
                onChange={dateOfReplacementDateTimeChangeHandler}
                format="dd/MM/yyyy HH:mm"
              />
            </LocalizationProvider>
          </Stack>
        </Grid>
        <Grid item xs={12} md={component.replacePermission === ReplacePermissionLevel.ServiceOnly ? 12 : 4}>
          <Stack direction="row">
            {component.replacePermission === ReplacePermissionLevel.ServiceOnly && (
              <TextField
                variant="standard"
                label={t("label-replacement-person")}
                value={technician}
                onChange={technicianChangeHandler}
                fullWidth
                data-testid="input-replacer-person"
              />
            )}
            <Box
              width="min-content"
              sx={(theme) => ({ position: "relative", alignContent: "end", height: theme.spacing(6) })}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({ color: theme.palette.grey[700], position: "absolute", top: 0, left: 0 })}
              >
                {t("label-replacement-rating")}
              </Typography>

              <Rating value={rating} onChange={ratingChangeHandler} />
            </Box>
          </Stack>
        </Grid>
        {component.replacePermission === ReplacePermissionLevel.ServiceOnly && (
          <Grid item xs={12}>
            <TextField
              variant="standard"
              multiline
              rows={3}
              label={t("label-replacement-feedback")}
              value={feedback}
              onChange={feedbackChangeHandler}
              fullWidth
              data-testid="input-replacement-feedback"
            />
          </Grid>
        )}
      </Grid>
      {!hideSaveButton &&
        <Stack alignItems="end" pt={1}>
          <Button
            startIcon={<SaveIcon />}
            onClick={() => resetAndReplaceClickHandler(component, dateOfReplacement, technician, rating, feedback)}
            disabled={replaceHardwareParams.replaceStatus === HttpFetchStatus.Loading}
          >
            {t("button-reset-and-save-replacement")}
          </Button>
        </Stack>
      }
    </React.Fragment>
  );
};

const TextWithLabel: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  return (
    <React.Fragment>
      <InputLabel shrink>{label}</InputLabel>
      <Typography>{text}</Typography>
    </React.Fragment>
  );
};

export default ReplaceLifetime;